import { Bot, Database, Boxes, Workflow, Plug, Route, Terminal, BrainCircuit, FlaskConical, Radar, Megaphone, ShoppingBag, Inbox, Building2, PhoneCall } from 'lucide-react'

// The public service catalog — name, price, icon, and a real description for the
// detail popup. Keys match the backend service keys.
export const CATALOG = [
  {
    key: 'answers', name: 'AI Support Chatbot', price: 39, icon: Bot,
    short: 'A trained bot that lives on your site as a chat bubble.',
    desc: 'A chat bubble on your website, trained on your own pages. It answers visitors instantly — products, pricing, policies, FAQs — and never invents facts or fakes a handoff. When it can’t help, it captures a real lead (name, email, phone) for you.',
    example: 'A visitor asks “do you ship to Canada?” and it answers straight from your shipping page.',
    onboard: 'Feed it whatever your visitors ask about — your website, your FAQ/policy PDFs, or a help centre. That becomes the only thing it answers from.',
    needsContent: true,
  },
  {
    key: 'sync', name: 'Knowledge Sync', price: 19, icon: Database,
    short: 'An always-fresh, searchable copy of your content.',
    desc: 'An always-fresh, searchable copy of your website/content that your apps can query — plus an “Ask your content” box that returns a plain-language answer with its sources. It re-crawls on a schedule so search never goes stale.',
    example: 'Search “reset password” and get the exact help section — even if the page never uses that word.',
    onboard: 'Point it at the content you want searchable — your site, a database, a cloud bucket, or Confluence/Jira. It keeps an always-fresh copy.',
    needsContent: true,
  },
  {
    key: 'rag', name: 'Custom RAG API', price: 29, icon: Boxes,
    short: 'A private “chat with your docs” query API.',
    desc: 'Turn a set of documents into a private Q&A brain with a query API. Your docs are split into passages and embedded; answers are written strictly from them, with citations. You can tune the chunking, embedding model, and match method per collection.',
    example: 'Ask “how long is the return window?” and get the answer plus the exact policy passage it came from.',
    onboard: 'RAG organises content into tuneable collections — open RAG, create a collection, then add the documents you want a private Q&A brain over.',
    needsContent: true, ownContent: true,
  },
  {
    key: 'orchestration', name: 'Agentic Workflows', price: 49, icon: Workflow,
    short: 'Automations like escalation and lead capture.',
    desc: 'Automations built from your content — “escalate to a human”, “capture a lead” — that run on a trigger, not a passive chatbot. Chain steps into a workflow you can call from your app via API.',
    example: 'A high-intent question automatically captures the lead and notifies your team.',
    onboard: 'No content needed to start — open Workflows to wire up your first trigger. Any content you add here makes its steps smarter.',
  },
  {
    key: 'middleware', name: 'Integration Adapters', price: 49, icon: Plug,
    short: 'Turn inbound webhooks into your AI pipeline.',
    desc: 'Turn inbound webhooks — forms, other tools — into your AI pipeline, so existing systems flow straight into your knowledge base and can trigger actions. Connect what you already use without custom glue code.',
    example: 'A form submission is routed into your AI pipeline and answered or escalated automatically.',
    onboard: 'Open Adapters to grab your inbound webhook URL and point your form/tool at it.',
  },
  {
    key: 'intake', name: 'Multi-Specialty Intake', price: 59, icon: Route,
    short: 'Route questions to the right specialty desk.',
    desc: 'A smart receptionist that routes each inbound question to the right specialty desk, checks for conflicts, and escalates emergencies to a human first. Vertical-agnostic — law, medical, accounting, and more.',
    example: '“Family history of breast cancer, what screening?” → routed to the Cancer Screening desk.',
    onboard: 'Add the content each desk answers from below, then open Intake to name your specialty desks and routing rules.',
    needsContent: true,
  },
  {
    key: 'voice', name: 'Voice AI Receptionist', price: 89, icon: PhoneCall,
    short: 'An AI that answers your phone and routes the call.',
    desc: 'An AI phone agent that answers your calls by voice, understands the caller in their own language, routes them to the right department, and hands off to a human when a call needs one — grounded in your content, never invented.',
    example: 'A caller says “I have a billing question” and is routed straight to the billing desk — in their own language.',
    onboard: 'Point a phone number (SignalWire/Twilio) at ReRight and name the desks calls can route to; add the content each desk answers from.',
    needsContent: true,
  },
  {
    key: 'agent', name: 'Tool-using Agent / MCP', price: 29, icon: Terminal,
    short: 'An agent over your knowledge base — CLI, MCP, REST.',
    desc: 'An AI agent over your knowledge base you can drive from Claude, Cursor, the CLI, or plain REST. It answers from your content and can use tools to get things done.',
    example: 'Drive it from the CLI: reright agent run "summarize this week’s support tickets".',
    onboard: 'Give it a knowledge base to work over — add content below — then grab your API key in Setup to drive it from the CLI/MCP.',
    needsContent: true,
  },
  {
    key: 'marketing-intel', name: 'Marketing Intelligence Brain', price: 79, icon: BrainCircuit,
    short: 'Evidence-first answers across all your marketing data.',
    desc: 'Connect your ads, analytics, SEO, CRM, and commerce data. The Brain answers "why did revenue change" with source-linked evidence, forecasts where you\'re heading, and hands you the next move.',
    example: 'Ask "which channel drove last week\'s signups?" and get the number, the source, and what to do next.',
    onboard: 'Open Connections and wire your sources (Google Search Console, GA4, Google Ads, Meta, Stripe). The Brain reasons across whatever you connect.',
  },
  {
    key: 'growth', name: 'Growth Lab', price: 59, icon: FlaskConical,
    short: 'Turn funnel friction into growth experiments.',
    desc: 'Detects funnel friction from your real metrics and turns it into evidence-backed, human-approved growth experiments grounded in behavioral science — never dark patterns.',
    example: 'Flags a drop between add-to-cart and checkout, then drafts an A/B test to fix it.',
    onboard: 'Connect your funnel data, then open Growth Lab to scan for friction and draft your first experiment.',
  },
  {
    key: 'seo', name: 'SEO Intelligence', price: 69, icon: Radar,
    short: 'Search + AI-answer-engine visibility, one surface.',
    desc: 'Authority, keyword rankings, backlinks (with toxic flags), competitor gaps, site audit — plus AI-visibility: whether ChatGPT/Claude/Gemini/Perplexity cite you. Measures discoverability and helps manufacture it.',
    example: 'See you\'re cited by 0/5 AI engines, then draft the answer pages that get you cited.',
    onboard: 'Connect Google Search Console (free) for live rankings; the SEO agent handles the rest.',
  },
  {
    key: 'campaigns', name: 'WhatsApp Campaigns', price: 49, icon: Megaphone,
    short: 'Broadcast approved templates to opted-in segments.',
    desc: 'Segment your audience by tag, broadcast approved WhatsApp templates with rate-limits and consent enforcement, and track every delivery — with a built-in CRM.',
    example: 'Send a launch broadcast to opted-in "VIP" contacts and watch delivery/read rates live.',
    onboard: 'Connect WhatsApp + import contacts, then open Campaigns to build your first broadcast.',
  },
  {
    key: 'commerce', name: 'WhatsApp Commerce', price: 59, icon: ShoppingBag,
    short: 'Sell in chat with a catalog and real Stripe checkout.',
    desc: 'Answer product questions grounded in your catalog, and when a shopper wants to buy, create an order and hand back a real Stripe payment link — sell over WhatsApp or web chat.',
    example: 'A shopper asks about a product in chat and checks out with a Stripe link, no app switch.',
    onboard: 'Add your catalog + connect Stripe, then open Commerce to start selling in chat.',
  },
  {
    key: 'inbox', name: 'Team Inbox', price: 29, icon: Inbox,
    short: 'Shared inbox with AI-suggested reply drafts.',
    desc: 'A shared team inbox with assignment and open/assigned/closed workflow, replies over the visitor\'s own channel, and AI-suggested, KB-grounded reply drafts your agents review and send.',
    example: 'An agent opens a conversation and gets a suggested reply grounded in your knowledge base.',
    onboard: 'Open Inbox to start working conversations; connect your KB so suggested replies are grounded.',
  },
  {
    key: 'reseller', name: 'White-label / Reseller', price: 199, icon: Building2,
    short: 'Run the whole platform under your own brand.',
    desc: 'Run the entire platform under your own brand for your clients — your logo, your domain, your pricing. Manage client workspaces from one place.',
    example: 'An agency offers "their" AI growth suite to clients, powered by ReRight underneath.',
    onboard: 'Open White-label to set your brand, domain, and client pricing.',
  },
]

// Which services you can run several of (each instance = one paid seat). Sync
// (one searchable KB) and Agent (one agent over it) are one-per-workspace.
// Mirrors the backend's SINGLETON_SERVICES in services/activation.js.
const SINGLETON = new Set(['sync', 'agent', 'marketing-intel', 'growth', 'seo', 'commerce', 'inbox', 'reseller'])
CATALOG.forEach((s) => { s.seated = !SINGLETON.has(s.key) })
export const isSeated = (k) => !SINGLETON.has(k)
// What one instance of each service is called, for "3 chatbots" style copy.
export const UNIT = {
  answers: 'chatbot', rag: 'collection', orchestration: 'workflow',
  middleware: 'adapter', intake: 'desk', voice: 'line', sync: 'workspace', agent: 'agent',
  'marketing-intel': 'brain', growth: 'lab', seo: 'workspace', campaigns: 'campaign',
  commerce: 'store', inbox: 'inbox', reseller: 'brand',
}
export const unitOf = (k) => UNIT[k] || 'instance'

export const byKey = (k) => CATALOG.find((s) => s.key === k)
